import type { ExportConfig, GeneratedFile, LanguageGenerator } from './types';
import { generateEnv, toPascal, toCamel } from './utils';

function phpType(t: string): string {
  return t === 'number' ? 'float' : t === 'boolean' ? 'bool' : t === 'array' ? 'array' : 'string';
}

function genConfig(config: ExportConfig): string {
  const appEntries = config.apps
    .map((a) => `        '${a.key}' => ['from_email' => '${a.fromEmail}', 'key_env' => '${a.envVarName}']`)
    .join(",\n");

  const templateEntries = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const doc = t.schema
          ? `        // payload: ${t.schema.map((f) => f.key + (f.required ? '' : '?')).join(', ')}\n`
          : '';
        return `${doc}        '${t.camelName}' => ['app' => '${a.key}', 'slug' => '${t.slug}']`;
      }),
    )
    .join(",\n\n");

  return `<?php
// mail_config.php — generated by Mail Service SDK Export

return [
    'apps' => [
${appEntries},
    ],
    'templates' => [
${templateEntries},
    ],
];
`;
}

function genService(config: ExportConfig): string {
  const typedMethods = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const methodName = `send${toPascal(t.camelName)}`;
        if (t.schema && t.schema.length > 0) {
          const params = t.schema
            .map((f) => `${phpType(f.type)} $${toCamel(f.key)}${f.required ? '' : ' = null'}`)
            .join(', ');
          const dataArray = t.schema
            .map((f) => `'${f.key}' => $${toCamel(f.key)}`)
            .join(', ');
          return `    public function ${methodName}(string $recipient, ${params}): array\n    {\n        return $this->send('${t.camelName}', $recipient, [${dataArray}]);\n    }`;
        }
        return `    public function ${methodName}(string $recipient, array $data = []): array\n    {\n        return $this->send('${t.camelName}', $recipient, $data);\n    }`;
      }),
    )
    .join("\n\n");

  return `<?php
// mail_service.php — generated by Mail Service SDK Export

class MailService
{
    private array $config;
    private string $serviceUrl;

    public function __construct()
    {
        $this->config = require __DIR__ . '/mail_config.php';
        $this->serviceUrl = rtrim($_ENV['${config.serviceUrlVarName}'] ?? getenv('${config.serviceUrlVarName}') ?? '', '/');
    }

    private function getKey(string $app): string
    {
        $env = $this->config['apps'][$app]['key_env'];
        return $_ENV[$env] ?? getenv($env) ?? '';
    }

    private function isReady(string $app): bool
    {
        return !empty($this->serviceUrl) && !empty($this->getKey($app));
    }

    private function post(string $app, string $path, array $body): array
    {
        if (!$this->isReady($app)) {
            return ['success' => false, 'error' => 'Not configured'];
        }
        $ch = curl_init($this->serviceUrl . $path);
        curl_setopt_array($ch, [
            CURLOPT_POST           => true,
            CURLOPT_RETURNTRANSFER => true,
            CURLOPT_HTTPHEADER     => [
                'Content-Type: application/json',
                'X-API-KEY: ' . $this->getKey($app),
            ],
            CURLOPT_POSTFIELDS => json_encode($body),
        ]);
        $result = curl_exec($ch);
        curl_close($ch);
        return json_decode($result ?: '{}', true) ?? ['success' => false];
    }

    public function send(string $template, string $recipient, array $data = []): array
    {
        $t = $this->config['templates'][$template];
        return $this->post($t['app'], '/v1/send', [
            'template_slug' => $t['slug'],
            'recipient'     => $recipient,
            'data'          => $data,
        ]);
    }

    public function sendRaw(string $app, string $subject, string $html, string $recipient, string $fromName = ''): array
    {
        return $this->post($app, '/v1/send/raw', [
            'subject' => $subject, 'html' => $html, 'recipient' => $recipient, 'from_name' => $fromName,
        ]);
    }

${typedMethods}
}
`;
}

export const phpGenerator: LanguageGenerator = {
  id: 'php',
  name: 'PHP',
  category: 'full-sdk',
  color: '#777BB4',
  generate(config: ExportConfig): GeneratedFile[] {
    return [
      { filename: 'mail_config.php', content: genConfig(config), syntax: 'php' },
      { filename: 'MailService.php', content: genService(config), syntax: 'php' },
      { filename: '.env', content: generateEnv(config), syntax: 'shell' },
    ];
  },
};
